import React, { useState, useEffect } from "react";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Typography,
  Avatar,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogContent,
  Grid,
  Paper,
  ListItemButton,
  Slide,
} from "@mui/material";
import {
  QrCode,
  ArrowLeft,
  ArrowRight,
  Phone,
  MapPin,
  Download,
  X,
  UserPlus,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import logo from "./image/logoN.jpg"; 
import FacebookOutlinedIcon from "@mui/icons-material/FacebookOutlined";
import InstagramIcon from "@mui/icons-material/Instagram";
import ShareIcon from "@mui/icons-material/Share";
// ==========================================
// TRANSLATIONS DICTIONARY
// ==========================================
const t = {
  en: {
    title: "Al Nawfaly Team",
    subtitle: "Tap a team member to view their card",
    shareBtn: "Share Directory",
    connect: "Connect & Find Us",
    teamMember: "Team Member",
    saveContact: "Save to Contacts",
    contactInfo: "Contact Info",
    primaryMobile: "Primary Mobile",
    secondaryPhone: "Secondary Phone",
    qrTitle: "Directory QR Code",
    scanText1: "Scan this code to instantly open the ",
    scanText2: "Team Directory",
    scanText3: " on any phone.",
    downloadImage: "Download Image",
    langToggle: "عربي", // Text to show when in English
  },
  ar: {
    title: "حدادة النوفلي ",
    subtitle: "انقر على اسم عضو لعرض بطاقته",
    shareBtn: "مشاركة الدليل",
    connect: "تواصل معنا",
    teamMember: "عضو الفريق",
    saveContact: "حفظ في جهات الاتصال",
    contactInfo: "معلومات الاتصال",
    primaryMobile: "الهاتف الأساسي",
    secondaryPhone: "الهاتف الثانوي",
    qrTitle: "رمز الدليل (QR)",
    scanText1: "امسح هذا الرمز لفتح ",
    scanText2: "دليل الفريق",
    scanText3: " فوراً على أي هاتف.",
    downloadImage: "تحميل الصورة",
    langToggle: "English", // Text to show when in Arabic
  },
};

// ==========================================
// DATA CONFIGURATION
// ==========================================
const companyInfo = {
  name: "Al Nawfaly Corp",
  nameAr: "حدادة النوفلي ",
  logo: logo,
  links: {
    facebook: "https://www.facebook.com/share/1VuEBu1xcg/?mibextid=wwXIfr",
    instagram: "https://www.instagram.com/8.craft?igsh=MW9lenI2cDVrNGt0NA==",
    maps: "https://maps.app.goo.gl/UoN3PUHxt9inbSpb6?g_st=ic",
  },
};

const teamData = {
  nawfal: {
    id: "nawfal",
    fName: "Nawfal",
    lName: "Nawfaly",
    fNameAr: "الحاج نوفل ",
    lNameAr: "النوفلي",
    description: "Manager",
    descriptionAr: "إدارة المعمل ",
    phone1: "+964 0772 203 4096",
    phone2: "+964 0750 402 8776",
  },
  wadaah: {
    id: "wadaah",
    fName: "Wadaah",
    lName: "Nawfaly",
    fNameAr: "وضاح",
    lNameAr: "النوفلي",
    description: "General Blacksmithing, Doors & Windows",
    descriptionAr: "الحدادة العامة والأبواب والشبابيك",
    phone1: "+964 770 521 2687",
    phone2: "+964 750 919 4044",
  },
  radwaan: {
    id: "radwaan",
    fName: "Radwaan",
    lName: "Nawfaly",
    fNameAr: "رضوان",
    lNameAr: "النوفلي",
    description: "Iron Bending, Shaping & Forming",
    descriptionAr: "قص وثني وتشكيل الحديد",
    phone1: "+964 770 167 6135",
    phone2: "+964 750 722 9130",
  },
  rayan: {
    id: "rayan",
    fName: "Rayan",
    lName: "Nawfaly",
    fNameAr: "ريان",
    lNameAr: "النوفلي",
    description: "Engineering design & CNC",
    descriptionAr: "التصميم الهندسي وال CNC",
    phone1: "+964 780 037 0616",
    phone2: "",
  },
};

// ==========================================
// MUI THEME SETUP
// ==========================================
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: "#020617",
      paper: "#0f172a",
    },
    primary: { main: "#3b82f6" },
    secondary: { main: "#8b5cf6" },
    text: { primary: "#f8fafc", secondary: "#94a3b8" },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Tajawal", "Inter", sans-serif', // Added Tajawal/Arabic fonts
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: 12,
          padding: "12px 24px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
  },
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ==========================================
// MAIN COMPONENT
// ==========================================
export default function App() {
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  

  // 👇 CHANGED FROM "en" TO "ar" HERE 👇
  const [lang, setLang] = useState("ar");

  // Helper to get text based on language
  const text = t[lang];
  const isAr = lang === "ar";

  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get("user");
      if (userId && teamData[userId]) {
        setCurrentUserId(userId);
      } else {
        setCurrentUserId(null);
      }
    };
    handleUrlChange();
    window.addEventListener("popstate", handleUrlChange);
    return () => window.removeEventListener("popstate", handleUrlChange);
  }, []);

  const handleUserSelect = (userId) => {
    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?user=${userId}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
    setCurrentUserId(userId);
  };

  const handleGoBack = () => {
    const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    window.history.pushState({ path: newUrl }, "", newUrl);
    setCurrentUserId(null);
  };

  const downloadVCard = (user) => {
    // Uses English data for the vCard format to ensure phone compatibility
    let vcard = `BEGIN:VCARD\nVERSION:3.0\nN:${user.lName};${user.fName};;;\nFN:${user.fName} ${user.lName}\n`;
    vcard += `ORG:${companyInfo.name}\n`;
    if (user.phone1) vcard += `TEL;TYPE=CELL:${user.phone1}\n`;
    if (user.phone2) vcard += `TEL;TYPE=WORK:${user.phone2}\n`;
    vcard += `URL:${companyInfo.links.facebook}\n`;
    vcard += `URL:${companyInfo.links.instagram}\n`;
    vcard += `END:VCARD`;

    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${user.fName}_${user.lName}_Contact.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadQR = () => {
    const canvas = document.getElementById("qr-code-canvas");
    if (!canvas) return;
    const pngUrl = canvas.toDataURL("image/png");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `Al-Nawfaly-Directory-QR.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const glassStyles = {
    background: "rgba(30, 41, 59, 0.6)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: 4,
    transition: "all 0.3s ease",
    "&:hover": {
      background: "rgba(30, 41, 59, 0.9)",
      borderColor: "rgba(255, 255, 255, 0.15)",
    },
  };

  const handleShare = async () => {
    const canvas = document.getElementById("qr-code-canvas");
    // const shareUrl = "https://al-nawfaly-cop.vercel.app/";
    const shareUrl = " https://contact.alnawfely.com/";
    if (!canvas) return;

    // 1. Check if the browser supports the Web Share API at all
    if (navigator.share) {
      try {
        const dataUrl = canvas.toDataURL("image/png");
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], "Al-Nawfaly-QR.png", {
          type: "image/png",
        });

        // Try to share as a file (Image)
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: isAr ? companyInfo.nameAr : companyInfo.name,
            text: text.scanText1 + text.scanText2,
          });
        } else {
          // Fallback to sharing just the link via the share menu
          await navigator.share({
            title: isAr ? companyInfo.nameAr : companyInfo.name,
            url: shareUrl,
          });
        }
      } catch (err) {
        // This block usually triggers if the user cancels the share popup manually.
        // We do nothing here so it doesn't "copy" just because they hit 'Cancel'.
        console.log("User cancelled or share failed:", err);
      }
    } else {
      // 2. Browser does NOT support sharing (e.g., Chrome on Desktop)
      // Directly copy the link to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert(isAr ? "تم نسخ الرابط إلى الحافظة" : "Link copied to clipboard!");
      } catch (copyErr) {
        console.error("Clipboard copy failed:", copyErr);
      }
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          bgcolor: "#020617",
        }}
      >
        <Box
          // Apply RTL direction dynamically based on language
          dir={isAr ? "rtl" : "ltr"}
          sx={{
            width: "100%",
            maxWidth: 440,
            bgcolor: "background.paper",
            position: "relative",
            overflow: "hidden",
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Global Language Toggle */}
          <Button
            onClick={() => setLang(isAr ? "en" : "ar")}
            sx={{
              position: "absolute",
              top: 16,
              [isAr ? "left" : "right"]: 16,
              zIndex: 50,
              bgcolor: "rgba(255,255,255,0.1)",
              color: "white",
              backdropFilter: "blur(4px)",
              borderRadius: 8,
              py: 0.5,
              px: 2,
              minWidth: "auto",
            }}
          >
            {text.langToggle}
          </Button>

          {/* ========================================== */}
          {/* VIEW 1: DIRECTORY                          */}
          {/* ========================================== */}
          {!currentUserId && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                p: 3,
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 6,
                  mb: 4,
                }}
              >
                <Avatar
                  src={companyInfo.logo}
                  sx={{
                    width: 110,
                    height: 110,
                    bgcolor: "white",
                    p: 1,
                    boxShadow: "0 8px 32px rgba(59, 130, 246, 0.2)",
                    mb: 3,
                  }}
                />
                <Typography
                  variant="h5"
                  color="text.primary"
                  align="center"
                  gutterBottom
                >
                  {text.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="center"
                  mb={3}
                >
                  {text.subtitle}
                </Typography>

                <Button
                  variant="outlined"
                  startIcon={
                    <QrCode
                      size={18}
                      style={{
                        marginRight: isAr ? 0 : 8,
                        marginLeft: isAr ? 8 : 0,
                      }}
                    />
                  }
                  onClick={() => setIsQrModalOpen(true)}
                  sx={{
                    borderColor: "rgba(255,255,255,0.1)",
                    color: "white",
                    borderRadius: 8,
                    "&:hover": {
                      borderColor: "rgba(255,255,255,0.3)",
                      bgcolor: "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  {text.shareBtn}
                </Button>
              </Box>

              <List
                sx={{
                  mb: "auto",
                  gap: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {Object.values(teamData).map((user) => (
                  <Paper key={user.id} elevation={0} sx={glassStyles}>
                    <ListItemButton
                      onClick={() => handleUserSelect(user.id)}
                      sx={{ p: 2, borderRadius: 4 }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: "primary.dark",
                            width: 48,
                            height: 48,
                            ml: isAr ? 2 : 0,
                            mr: isAr ? 0 : 2,
                            background:
                              "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                          }}
                        >
                          {isAr ? user.fNameAr[0] : user.fName[0]}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ textAlign: isAr ? "right" : "left" }}
                        primary={
                          <Typography
                            variant="subtitle1"
                            fontWeight="bold"
                            color="white"
                          >
                            {isAr
                              ? `${user.fNameAr} ${user.lNameAr}`
                              : `${user.fName} ${user.lName}`}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {isAr ? user.descriptionAr : user.description}
                          </Typography>
                        }
                      />
                      {isAr ? (
                        <ChevronLeft size={20} color="#64748b" />
                      ) : (
                        <ChevronRight size={20} color="#64748b" />
                      )}
                    </ListItemButton>
                  </Paper>
                ))}
              </List>

              <Box
                sx={{
                  mt: 5,
                  pt: 4,
                  borderTop: "1px solid rgba(255,255,255,0.05)",
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 1.5,
                    display: "block",
                    textAlign: "center",
                    mb: 3,
                  }}
                >
                  {text.connect}
                </Typography>
                <Grid
                  container
                  spacing={3}
                  justifyContent="center"
                  alignItems="center"
                >
                  {[
                    {
                      icon: <FacebookOutlinedIcon sx={{ fontSize: 28 }} />,
                      label: "Facebook",
                      url: companyInfo.links.facebook,
                      color: "#3b82f6",
                    },
                    {
                      icon: <InstagramIcon sx={{ fontSize: 28 }} />,
                      label: "Instagram",
                      url: companyInfo.links.instagram,
                      color: "#ec4899",
                    },
                    {
                      icon: <MapPin size={28} />,
                      label: "Location",
                      url: companyInfo.links.maps,
                      color: "#10b981",
                    },
                  ].map((social, idx) => (
                    <Grid
                      item
                      xs={4}
                      key={idx}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <Paper
                        component="a"
                        href={social.url}
                        target="_blank"
                        elevation={0}
                        sx={{
                          ...glassStyles,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          p: 3,
                          width: "100%",
                          textDecoration: "none",
                        }}
                      >
                        <Box
                          sx={{ color: social.color, mb: 1, display: "flex" }}
                        >
                          {social.icon}
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={500}
                          align="center"
                        >
                          {social.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          )}

          {/* ========================================== */}
          {/* VIEW 2: PROFILE                            */}
          {/* ========================================== */}
          {currentUserId && teamData[currentUserId] && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  height: 180,
                  width: "100%",
                  background:
                    "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #0f172a 100%)",
                  position: "relative",
                }}
              >
                <IconButton
                  onClick={handleGoBack}
                  sx={{
                    position: "absolute",
                    top: 16,
                    [isAr ? "right" : "left"]: 16,
                    bgcolor: "rgba(0,0,0,0.3)",
                    color: "white",
                    backdropFilter: "blur(8px)",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.5)" },
                  }}
                >
                  {isAr ? <ArrowRight size={20} /> : <ArrowLeft size={20} />}
                </IconButton>
              </Box>

              <Box sx={{ px: 3, mt: -8, position: "relative", zIndex: 10 }}>
                <Avatar
                  src={`https://ui-avatars.com/api/?name=${teamData[currentUserId].fName}+${teamData[currentUserId].lName}&background=3b82f6&color=fff&size=200&bold=true`}
                  sx={{
                    width: 112,
                    height: 112,
                    border: "4px solid #0f172a",
                    bgcolor: "background.paper",
                    mb: 3,
                  }}
                />

                <Typography
                  variant="h4"
                  fontWeight={800}
                  color="white"
                  gutterBottom
                >
                  {isAr
                    ? `${teamData[currentUserId].fNameAr} ${teamData[currentUserId].lNameAr}`
                    : `${teamData[currentUserId].fName} ${teamData[currentUserId].lName}`}
                </Typography>
                
                <Box sx={{ display: "flex", alignItems: "center", mb: 4, mt: 2 }}>
                  <Avatar
                    src={companyInfo.logo}
                    sx={{
                      width: 20,
                      height: 20,
                      bgcolor: "white",
                      ml: isAr ? 1 : 0,
                      mr: isAr ? 0 : 1,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="primary.light"
                    fontWeight={600}
                    mt={.5}
                  >
                    {isAr
                      ? `${teamData[currentUserId].descriptionAr} `
                      : `${teamData[currentUserId].description}`}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  startIcon={
                    <UserPlus
                      size={20}
                      style={{
                        marginRight: isAr ? 0 : 8,
                        marginLeft: isAr ? 8 : 0,
                      }}
                    />
                  }
                  onClick={() => downloadVCard(teamData[currentUserId])}
                  sx={{
                    bgcolor: "white",
                    mb: 5,
                    py: 1.5,
                    color: "#0f172a",
                    "&:hover": { bgcolor: "#f1f5f9" },
                  }}
                >
                  {text.saveContact}
                </Button>

                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 1.5,
                    display: "block",
                    mb: 2,
                  }}
                >
                  {text.contactInfo}
                </Typography>

                <List sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {teamData[currentUserId].phone1 && (
                    <Paper
                      component="a"
                      href={`tel:${teamData[currentUserId].phone1}`}
                      elevation={0}
                      sx={{ ...glassStyles, textDecoration: "none", p: 0 }}
                    >
                      <ListItem sx={{ p: 2 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: "rgba(30, 41, 59, 0.8)",
                              color: "#60a5fa",
                            }}
                          >
                            <Phone size={20} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          sx={{ textAlign: isAr ? "right" : "left" }}
                          primary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              {text.primaryMobile}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="body1"
                              color="white"
                              fontWeight="bold"
                              dir="ltr"
                              sx={{ textAlign: isAr ? "right" : "left" }}
                            >
                              {teamData[currentUserId].phone1}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </Paper>
                  )}

                  {teamData[currentUserId].phone2 && (
                    <Paper
                      component="a"
                      href={`tel:${teamData[currentUserId].phone2}`}
                      elevation={0}
                      sx={{ ...glassStyles, textDecoration: "none", p: 0 }}
                    >
                      <ListItem sx={{ p: 2 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: "rgba(30, 41, 59, 0.8)",
                              color: "#60a5fa",
                            }}
                          >
                            <Phone size={20} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          sx={{ textAlign: isAr ? "right" : "left" }}
                          primary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={500}
                            >
                              {text.secondaryPhone}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="body1"
                              color="white"
                              fontWeight="bold"
                              dir="ltr"
                              sx={{ textAlign: isAr ? "right" : "left" }}
                            >
                              {teamData[currentUserId].phone2}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </Paper>
                  )}
                </List>
              </Box>
            </Box>
          )}

          {/* ========================================== */}
          {/* QR CODE DIALOG                             */}
          {/* ========================================== */}
          <Dialog
            open={isQrModalOpen}
            onClose={() => setIsQrModalOpen(false)}
            TransitionComponent={Transition}
            PaperProps={{
              sx: {
                bgcolor: "#0f172a",
                borderRadius: 4,
                backgroundImage: "none",
                border: "1px solid rgba(255,255,255,0.1)",
              },
            }}
            maxWidth="xs"
            fullWidth
            dir={isAr ? "rtl" : "ltr"}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 3,
                pb: 1,
              }}
            >
              <Typography variant="h6" color="white" fontWeight="bold">
                {text.qrTitle}
              </Typography>
              <IconButton
                onClick={() => setIsQrModalOpen(false)}
                sx={{
                  color: "text.secondary",
                  bgcolor: "rgba(255,255,255,0.05)",
                }}
              >
                <X size={20} />
              </IconButton>
            </Box>

            <DialogContent
              sx={{
                p: 3,
                pt: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Paper
                sx={{
                  p: 3,
                  bgcolor: "white",
                  borderRadius: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 3,
                }}
              >
                <QRCodeCanvas
                  id="qr-code-canvas"
                  // value="https://al-nawfaly-cop.vercel.app/"
                  value="https://contact.alnawfely.com/"
                  size={200}
                  bgColor={"#ffffff"}
                  fgColor={"#0f172a"}
                  level={"H"}
                  
                />
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  {isAr ? companyInfo.nameAr : companyInfo.name} -{" "}
                  {isAr ? "دليل" : "Directory"}
                </Typography>
              </Paper>

              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                mb={3}
              >
                {text.scanText1}
                <Box component="span" fontWeight="bold" color="white">
                  {text.scanText2}
                </Box>
                {text.scanText3}
              </Typography>

              {/* Action Buttons Row */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={
                      <Download
                        size={18}
                        style={{
                          marginRight: isAr ? 0 : 8,
                          marginLeft: isAr ? 8 : 0,
                        }}
                      />
                    }
                    onClick={downloadQR}
                    sx={{ py: 1.5, fontSize: "0.85rem" }}
                  >
                    {isAr ? "تحميل" : "Download"}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={
                      <ShareIcon
                        sx={{
                          fontSize: 18,
                          marginRight: isAr ? 0 : 1,
                          marginLeft: isAr ? 1 : 0,
                        }}
                      />
                    }
                    onClick={handleShare}
                    sx={{
                      py: 1.5,
                      fontSize: "0.85rem",
                      borderColor: "rgba(255,255,255,0.1)",
                      color: "white",
                      "&:hover": { borderColor: "rgba(255,255,255,0.3)" },
                    }}
                  >
                    {isAr ? "مشاركة" : "Share"}
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
          </Dialog>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
